import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FaCheckCircle, FaTimes } from 'react-icons/fa';
import { WorkflowInputLayout } from './WorkflowInputLayout';
import HeatmapGrid from '../../../components/data_hub/HeatmapGrid';
import RateTable from '../../../components/data_hub/RateTable';
import { flattenTieredStrux } from '../../../hooks/useUtilityRates';
import { API_ENDPOINTS } from '../../../config/utilityApiConfig';
import { getMdhdWorkflowConfig, updateMdhdWorkflowConfig } from '../../../utils/mdhdWorkflowConfig';
import { getSelectedUtility } from '../../../utils/utilitySelection';
import { getUtilityWorkflowUiState } from '../../../utils/utilityWorkflowUiState';

const defaultEconomic = {
  touSource: 'library',
  touDatasetId: null,
  touRates: null,
  touRateMeta: null,
  touRateDetails: null,
  v2gMode: 'unmanaged',
  vehiclePricingDatasetId: null,
};

export default function RatePlanInputPage() {
  const [data, setData] = useState(() => ({
    ...defaultEconomic,
    ...getMdhdWorkflowConfig().ratePlan,
  }));
  const [loadingRates, setLoadingRates] = useState(false);
  const [rateError, setRateError] = useState('');
  const [availableRates, setAvailableRates] = useState([]);
  const [resolvedUtilityName, setResolvedUtilityName] = useState('');
  const [defaultRateId, setDefaultRateId] = useState(null);
  const [detailRateId, setDetailRateId] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const workflowUtility = getUtilityWorkflowUiState()?.mdhdAdoptionTrend?.selectedUtility;
  const fallbackUtility = getSelectedUtility()?.utilityName;
  const selectedUtility = workflowUtility || fallbackUtility || '';

  const updateData = (next) => setData((prev) => ({ ...prev, ...next }));

  useEffect(() => {
    updateMdhdWorkflowConfig('ratePlan', data);
  }, [data]);

  useEffect(() => {
    const run = async () => {
      if (!selectedUtility) {
        setResolvedUtilityName('');
        setAvailableRates([]);
        setDefaultRateId(null);
        setDetailRateId(null);
        setRateError('No utility selected in workflow context.');
        return;
      }

      const token = localStorage.getItem('authToken');
      if (!token) {
        setRateError('Missing auth token. Please log in again.');
        return;
      }

      setLoadingRates(true);
      setRateError('');
      try {
        const boundaryResponse = await axios.get(API_ENDPOINTS.getStopChainUtilityBoundary, {
          headers: { Authorization: `Token ${token}` },
          params: { utility_name: selectedUtility },
        });
        const territoryIds = boundaryResponse?.data?.map_context?.utility_territory_ids || [];
        if (!Array.isArray(territoryIds) || territoryIds.length === 0) {
          setAvailableRates([]);
          setDefaultRateId(null);
          setDetailRateId(null);
          setResolvedUtilityName(selectedUtility);
          setRateError('No utility territory mapping found for the selected utility.');
          return;
        }

        let resolvedDefaultRateId = null;
        let resolvedRateUtilityName = '';
        const defaultRateResponse = await axios.get(API_ENDPOINTS.getUtilityRateByTerritory, {
          headers: { Authorization: `Token ${token}` },
          params: { territory_id: territoryIds[0] },
        }).catch(() => null);
        if (defaultRateResponse?.data?.rate?.id != null) {
          resolvedDefaultRateId = Number(defaultRateResponse.data.rate.id);
        }
        if (defaultRateResponse?.data?.rate?.utility_name) {
          resolvedRateUtilityName = String(defaultRateResponse.data.rate.utility_name).trim();
        }

        const bulkRatesResponse = await axios.get(API_ENDPOINTS.getUtilityRatesByUtility, {
          headers: { Authorization: `Token ${token}` },
          params: { utility_name: resolvedRateUtilityName || selectedUtility },
        });
        const detailedRates = (bulkRatesResponse?.data?.rates || [])
          .filter(Boolean)
          .sort((a, b) => {
            const sectorCmp = String(a?.sector || '').localeCompare(String(b?.sector || ''));
            if (sectorCmp !== 0) return sectorCmp;
            return String(a?.rate_name || '').localeCompare(String(b?.rate_name || ''));
          });

        if (detailedRates.length === 0) {
          setAvailableRates([]);
          setDefaultRateId(null);
          setDetailRateId(null);
          setResolvedUtilityName(selectedUtility);
          setRateError('No rates found in database for this utility.');
          return;
        }

        setAvailableRates(detailedRates);
        setDefaultRateId(resolvedDefaultRateId);
        const selectedRateId = Number(data?.touDatasetId || 0);
        if (selectedRateId && detailedRates.some((rate) => Number(rate?.id) === selectedRateId)) {
          setDetailRateId(selectedRateId);
        } else if (resolvedDefaultRateId && detailedRates.some((rate) => Number(rate?.id) === resolvedDefaultRateId)) {
          setDetailRateId(resolvedDefaultRateId);
        } else {
          setDetailRateId(Number(detailedRates[0]?.id || 0) || null);
        }
        setResolvedUtilityName(
          String(
            bulkRatesResponse?.data?.resolved_utility_name
            || detailedRates[0]?.utility_name
            || selectedUtility
          ).trim() || selectedUtility
        );
      } catch (error) {
        setAvailableRates([]);
        setDefaultRateId(null);
        setDetailRateId(null);
        setResolvedUtilityName(selectedUtility);
        setRateError(error?.response?.data?.error || error?.message || 'Failed to load utility rates.');
      } finally {
        setLoadingRates(false);
      }
    };
    run();
  }, [selectedUtility, data?.touDatasetId]);

  const handleSelectRate = (rate) => {
    if (!rate?.id) return;
    setDetailRateId(Number(rate.id));
    updateData({
      touSource: 'datahub',
      touDatasetId: rate.id,
      touRates: null,
      touRateMeta: {
        id: rate.id,
        utility_name: rate.utility_name,
        rate_name: rate.rate_name,
        sector: rate.sector,
        effective_date: rate.effective_date,
      },
      touRateDetails: rate.rate_details || null,
    });
  };

  const defaultRate = useMemo(
    () => availableRates.find((rate) => Number(rate?.id) === Number(defaultRateId)) || null,
    [availableRates, defaultRateId]
  );
  const detailRate = useMemo(
    () => availableRates.find((rate) => Number(rate?.id) === Number(detailRateId)) || null,
    [availableRates, detailRateId]
  );
  const detailEnergyRows = useMemo(
    () => flattenTieredStrux(detailRate?.rate_details?.energyRateStrux, 'energyRateTiers'),
    [detailRate]
  );

  const openDetailModal = (rateId) => {
    setDetailRateId(Number(rateId));
    setIsDetailModalOpen(true);
  };

  return (
    <WorkflowInputLayout title="Rate plan">
      <div className="space-y-3">
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900 font-ui">
          Configure the MDHD EV rate for the utility currently selected in the workflow context.
          {selectedUtility && (
            <span className="ml-1 font-semibold">Selected utility: {selectedUtility}</span>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-lg font-ui p-4 space-y-4 h-[calc(100vh-260px)] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-800">Available utility rates</h3>
            {loadingRates && <span className="text-sm text-gray-500">Loading rates...</span>}
          </div>

          {rateError && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {rateError}
            </div>
          )}

          {!loadingRates && availableRates.length === 0 && !rateError && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-600">
              No rates found for this utility.
            </div>
          )}

          {defaultRate && (
            <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-emerald-800">Default rate</div>
                  <div className="text-base font-semibold text-gray-900">{defaultRate.rate_name}</div>
                  <div className="text-sm text-gray-700">
                    {resolvedUtilityName || defaultRate.utility_name}
                    {defaultRate.sector ? ` · ${defaultRate.sector}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => openDetailModal(defaultRate.id)}
                    className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                  >
                    See details
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSelectRate(defaultRate)}
                    className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                      Number(data?.touDatasetId) === Number(defaultRate?.id)
                        ? 'bg-green-100 text-green-800 border border-green-300'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {Number(data?.touDatasetId) === Number(defaultRate?.id) && <FaCheckCircle className="text-green-700" />}
                    {Number(data?.touDatasetId) === Number(defaultRate?.id) ? 'Selected' : 'Use this rate'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="rounded-lg border border-gray-200 flex-1 min-h-0">
            <div className="h-full overflow-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">Rate name</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">Sector</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {availableRates.map((rate) => {
                    const isSelectedRate = Number(data?.touDatasetId) === Number(rate?.id);
                    const isDefaultRate = Number(defaultRateId) === Number(rate?.id);
                    return (
                      <tr key={`utility-rate-row-${rate.id}`}>
                        <td className="px-3 py-2 text-gray-900">
                          {rate.rate_name}
                          {isDefaultRate && (
                            <span className="ml-2 inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800">
                              default
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-700">{rate.sector || '—'}</td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => openDetailModal(rate.id)}
                              className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                            >
                              See details
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSelectRate(rate)}
                              className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                                isSelectedRate
                                  ? 'bg-green-100 text-green-800 border border-green-300'
                                  : 'bg-blue-600 text-white hover:bg-blue-700'
                              }`}
                            >
                              {isSelectedRate && <FaCheckCircle className="text-green-700" />}
                              {isSelectedRate ? 'Selected' : 'Use rate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {isDetailModalOpen && detailRate && (
        <div className="fixed inset-0 z-[1200] bg-black/45 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[88vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <div className="text-lg font-bold text-gray-900">{detailRate.rate_name}</div>
                <div className="text-sm text-gray-600">
                  {resolvedUtilityName || detailRate.utility_name}
                  {detailRate.sector ? ` · ${detailRate.sector}` : ''}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsDetailModalOpen(false)}
                className="p-2 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                aria-label="Close details"
              >
                <FaTimes />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-4 space-y-4 bg-gray-50">
              {detailEnergyRows.length > 0 && (
                <div className="rounded-md border border-gray-200 bg-white p-3">
                  <RateTable
                    data={detailEnergyRows}
                    title="TOU period pricing"
                    columns={[
                      { key: 'period', label: 'Period', format: 'integer' },
                      { key: 'tier', label: 'Tier', format: 'integer' },
                      { key: 'max', label: 'Max Usage (kWh)', format: 'number' },
                      { key: 'rate', label: 'Rate ($/kWh)', format: 'currency' },
                      { key: 'adj', label: 'Adjustments ($/kWh)', format: 'currency' },
                    ]}
                  />
                </div>
              )}
              {Array.isArray(detailRate?.rate_details?.energyWeekdaySched) && detailRate.rate_details.energyWeekdaySched.length > 0 && (
                <div className="rounded-md border border-gray-200 bg-white p-3">
                  <HeatmapGrid
                    schedule={detailRate.rate_details.energyWeekdaySched}
                    title="Weekday TOU schedule (period legend included)"
                  />
                </div>
              )}
              {Array.isArray(detailRate?.rate_details?.energyWeekendSched) && detailRate.rate_details.energyWeekendSched.length > 0 && (
                <div className="rounded-md border border-gray-200 bg-white p-3">
                  <HeatmapGrid
                    schedule={detailRate.rate_details.energyWeekendSched}
                    title="Weekend TOU schedule (period legend included)"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </WorkflowInputLayout>
  );
}
